(() => {
  const ELEMENT_NAME = 'aoc-blog-archive';

  const ARTICLES_QUERY = `
    query BlogArticles(
      $first: Int!
      $after: String
      $searchQuery: String
    ) {
      articles(
        first: $first
        after: $after
        query: $searchQuery
        sortKey: PUBLISHED_AT
        reverse: true
      ) {
        nodes {
          id
          title
          onlineStoreUrl
          publishedAt
          excerpt
          content(truncateAt: 250)
          tags
          image {
            url
            altText
            width
            height
          }
          blog {
            title
            handle
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `;

  class AocBlogArchive extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;

      this.filterControls = Array.from(
        this.querySelectorAll('[data-filter-value]')
      );
      this.filterButtons = this.filterControls.filter((control) =>
        control.matches('button')
      );
      this.filterInputs = this.filterControls.filter((control) =>
        control.matches('input[type="checkbox"]')
      );
      this.filterLayout = this.dataset.filterLayout || 'pills';
      this.filterMatch = this.dataset.filterMatch === 'all'
        ? 'all'
        : 'any';
      this.dropdownDetails = this.querySelector(
        '[data-filter-dropdown-details]'
      );
      this.filterSummary = this.querySelector('[data-filter-summary]');
      this.filterApplyButton = this.querySelector('[data-filter-apply]');
      this.filterClearButton = this.querySelector('[data-filter-clear]');
      this.blogsWrapper = this.querySelector('.blogs-wrapper');
      this.statusElement = this.querySelector('.blogs-status');
      this.loadMoreButton = this.querySelector('.blogs-load-more');
      this.fallbackPlaceholder = this.querySelector(
        '.blogs-fallback-placeholder'
      );

      this.endpoint = this.dataset.endpoint || '';
      this.storefrontToken = this.dataset.storefrontToken || '';
      this.pageSize = this.getPositiveInteger(this.dataset.pageSize, 20);
      this.readMoreText = this.dataset.readMoreText || 'Read More';
      this.loadMoreText = this.dataset.loadMoreText || 'Load More';
      this.fallbackImage = this.getFallbackImage();

      const defaultFilter = this.dataset.defaultFilter || 'all';
      const defaultControlExists = this.filterControls.some(
        (control) =>
          this.normalizeTag(control.dataset.filterValue) ===
          this.normalizeTag(defaultFilter)
      );

      this.activeTags =
        defaultControlExists && this.normalizeTag(defaultFilter) !== 'all'
          ? [defaultFilter]
          : [];
      this.endCursor = null;
      this.hasNextPage = true;
      this.isLoading = false;
      this.activeController = null;
      this.renderedArticleIds = new Set();
      this.filterLabels = this.createFilterLabelMap();

      this.dateFormatter = new Intl.DateTimeFormat(
        document.documentElement.lang || 'en-US',
        {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        }
      );

      if (
        !this.blogsWrapper ||
        !this.statusElement ||
        !this.loadMoreButton
      ) {
        console.error('AOC blog archive: required section markup is missing.');
        return;
      }

      if (!this.endpoint || !this.storefrontToken) {
        console.error(
          'AOC blog archive: Storefront API endpoint or public token is missing.'
        );
        this.statusElement.textContent = 'Unable to load blog articles.';
        return;
      }

      if (this.filterLayout === 'dropdown') {
        this.filterInputs.forEach((input) => {
          input.addEventListener('change', () => {
            this.handleDropdownChange(input);
          });
        });

        this.filterApplyButton?.addEventListener('click', () => {
          this.applyDropdownFilters();
        });

        this.filterClearButton?.addEventListener('click', () => {
          this.clearDropdownFilters();
        });

        this.dropdownDetails?.addEventListener('toggle', () => {
          if (!this.dropdownDetails.open) this.updateActiveFilter();
        });
      } else {
        this.filterButtons.forEach((button) => {
          button.addEventListener('click', () => {
            this.selectFilter(button);
          });
        });
      }

      this.loadMoreButton.addEventListener('click', () => {
        this.loadArticles();
      });

      this.updateActiveFilter();
      this.loadArticles({ reset: true });
    }

    disconnectedCallback() {
      this.activeController?.abort();
    }

    getPositiveInteger(value, fallback) {
      const parsedValue = Number.parseInt(value, 10);

      return Number.isInteger(parsedValue) && parsedValue > 0
        ? parsedValue
        : fallback;
    }

    getFallbackImage() {
      const url = this.dataset.fallbackImage || '';

      if (!url) return null;

      return {
        url,
        alt: this.dataset.fallbackImageAlt || '',
        width: this.getPositiveInteger(
          this.dataset.fallbackImageWidth,
          null
        ),
        height: this.getPositiveInteger(
          this.dataset.fallbackImageHeight,
          null
        ),
      };
    }

    normalizeTag(tag) {
      return String(tag || '').trim().toLowerCase();
    }

    formatTitle(title) {
      return String(title || '')
        .trim()
        .toLocaleLowerCase()
        .replace(
          /(^|[\s/–—-])\p{L}/gu,
          (character) => character.toLocaleUpperCase()
        );
    }

    createFilterLabelMap() {
      const labels = new Map();

      this.filterControls.forEach((control) => {
        const value = control.dataset.filterValue;

        if (value && this.normalizeTag(value) !== 'all') {
          labels.set(
            this.normalizeTag(value),
            control.dataset.filterLabel || value
          );
        }
      });

      return labels;
    }

    buildSearchQuery(tags = []) {
      const uniqueTags = Array.from(
        new Map(
          tags
            .map((tag) => String(tag || '').trim())
            .filter(Boolean)
            .map((tag) => [this.normalizeTag(tag), tag])
        ).values()
      );

      if (uniqueTags.length === 0) return null;

      const clauses = uniqueTags.map(
        (tag) => `tag:${JSON.stringify(tag)}`
      );

      if (clauses.length === 1) return clauses[0];

      const operator = this.filterMatch === 'all' ? ' AND ' : ' OR ';

      return `(${clauses.join(operator)})`;
    }

    async fetchArticles({
      tags = [],
      after = null,
      first = this.pageSize,
      signal,
    } = {}) {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        signal,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Shopify-Storefront-Access-Token':
            this.storefrontToken.trim(),
        },
        body: JSON.stringify({
          query: ARTICLES_QUERY,
          variables: {
            first,
            after,
            searchQuery: this.buildSearchQuery(tags),
          },
        }),
      });

      let result;

      try {
        result = await response.json();
      } catch (error) {
        throw new Error('The Storefront API returned an invalid response.');
      }

      if (!response.ok || result.errors || !result.data?.articles) {
        console.error(
          'Storefront API error:',
          result.errors || result
        );
        throw new Error('Unable to retrieve blog articles.');
      }

      return result.data.articles;
    }

    getDisplayTags(article) {
      const displayTags = [];
      const usedLabels = new Set();

      (article.tags || []).forEach((tag) => {
        const label = this.filterLabels.get(this.normalizeTag(tag));

        if (label && !usedLabels.has(label)) {
          usedLabels.add(label);
          displayTags.push(label);
        }
      });

      if (displayTags.length === 0 && article.blog?.title) {
        displayTags.push(article.blog.title);
      }

      return displayTags;
    }

    createImageLink(article, articleUrl) {
      const imageLink = document.createElement('a');
      const hasArticleImage = Boolean(article.image?.url);
      const imageData = hasArticleImage
        ? article.image
        : this.fallbackImage;

      imageLink.className = 'article-card__image';
      imageLink.href = articleUrl;

      if (!hasArticleImage) {
        imageLink.classList.add('is-fallback');
      }

      if (imageData?.url) {
        const image = document.createElement('img');

        image.src = imageData.url;
        image.alt = hasArticleImage
          ? imageData.altText || article.title
          : imageData.alt || '';
        image.loading = 'lazy';
        image.decoding = 'async';

        if (imageData.width) image.width = imageData.width;
        if (imageData.height) image.height = imageData.height;

        imageLink.append(image);
      } else if (this.fallbackPlaceholder) {
        imageLink.append(
          this.fallbackPlaceholder.content.cloneNode(true)
        );
      }

      return imageLink;
    }

    createArticleCard(article) {
      const card = document.createElement('article');
      const articleUrl = article.onlineStoreUrl || '#';

      card.className = 'article-card';
      card.dataset.articleId = article.id;
      card.append(this.createImageLink(article, articleUrl));

      const content = document.createElement('div');
      content.className = 'article-card__content';

      const displayTags = this.getDisplayTags(article);

      if (displayTags.length > 0) {
        const tagsContainer = document.createElement('div');
        tagsContainer.className = 'article-card__tags';

        displayTags.forEach((tag) => {
          const tagElement = document.createElement('span');

          tagElement.className = 'article-card__tag';
          tagElement.textContent = tag;
          tagsContainer.append(tagElement);
        });

        content.append(tagsContainer);
      }

      const title = document.createElement('h3');
      const titleLink = document.createElement('a');

      title.className = 'article-card__title';
      titleLink.href = articleUrl;
      titleLink.textContent = this.formatTitle(article.title);
      title.append(titleLink);
      content.append(title);

      if (article.publishedAt) {
        const date = new Date(article.publishedAt);

        if (!Number.isNaN(date.getTime())) {
          const time = document.createElement('time');

          time.className = 'article-card__date';
          time.dateTime = article.publishedAt;
          time.textContent = this.dateFormatter.format(date);
          content.append(time);
        }
      }

      const excerptText = (
        article.excerpt ||
        article.content ||
        ''
      ).trim();

      if (excerptText) {
        const excerpt = document.createElement('p');

        excerpt.className = 'article-card__excerpt';
        excerpt.textContent = excerptText;
        content.append(excerpt);
      }

      const readMore = document.createElement('a');

      readMore.className = 'article-card__cta btn btn-primary';
      readMore.href = articleUrl;
      readMore.textContent = this.readMoreText;

      content.append(readMore);
      card.append(content);

      return card;
    }

    renderArticles(articles) {
      const fragment = document.createDocumentFragment();

      articles.forEach((article) => {
        if (this.renderedArticleIds.has(article.id)) return;

        this.renderedArticleIds.add(article.id);
        fragment.append(this.createArticleCard(article));
      });

      this.blogsWrapper.append(fragment);
    }

    updateActiveFilter() {
      const hasActiveTags = this.activeTags.length > 0;

      this.filterButtons.forEach((button) => {
        const value = button.dataset.filterValue;
        const normalizedValue = this.normalizeTag(value);
        const isActive = normalizedValue === 'all'
          ? !hasActiveTags
          : this.activeTags.some(
              (tag) => this.normalizeTag(tag) === normalizedValue
            );

        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });

      this.filterInputs.forEach((input) => {
        const value = input.dataset.filterValue;
        const normalizedValue = this.normalizeTag(value);

        input.checked = normalizedValue === 'all'
          ? !hasActiveTags
          : this.activeTags.some(
              (tag) => this.normalizeTag(tag) === normalizedValue
            );
      });

      if (this.filterSummary) {
        const activeLabels = this.activeTags.map(
          (tag) => this.filterLabels.get(this.normalizeTag(tag)) || tag
        );

        if (activeLabels.length === 0) {
          this.filterSummary.textContent = 'All';
        } else if (activeLabels.length <= 2) {
          this.filterSummary.textContent = activeLabels.join(', ');
        } else {
          this.filterSummary.textContent = `${activeLabels.length} selected`;
        }
      }
    }

    selectFilter(button) {
      const filterValue = button.dataset.filterValue || 'all';
      const selectedTags = this.normalizeTag(filterValue) === 'all'
        ? []
        : [filterValue];

      if (
        this.haveSameTags(selectedTags, this.activeTags) &&
        this.blogsWrapper.children.length > 0
      ) {
        return;
      }

      this.activeTags = selectedTags;
      this.updateActiveFilter();
      this.loadArticles({ reset: true });
    }

    haveSameTags(firstTags, secondTags) {
      const normalize = (tags) =>
        tags.map((tag) => this.normalizeTag(tag)).sort();
      const first = normalize(firstTags);
      const second = normalize(secondTags);

      return (
        first.length === second.length &&
        first.every((tag, index) => tag === second[index])
      );
    }

    getAllFilterInput() {
      return this.filterInputs.find(
        (input) => this.normalizeTag(input.dataset.filterValue) === 'all'
      );
    }

    getSelectedDropdownTags() {
      return this.filterInputs
        .filter(
          (input) =>
            input.checked &&
            this.normalizeTag(input.dataset.filterValue) !== 'all'
        )
        .map((input) => input.dataset.filterValue)
        .filter(Boolean);
    }

    handleDropdownChange(changedInput) {
      const allInput = this.getAllFilterInput();
      const isAllInput =
        this.normalizeTag(changedInput.dataset.filterValue) === 'all';

      if (isAllInput && changedInput.checked) {
        this.filterInputs.forEach((input) => {
          if (input !== changedInput) input.checked = false;
        });
        return;
      }

      if (!isAllInput && changedInput.checked && allInput) {
        allInput.checked = false;
      }

      if (this.getSelectedDropdownTags().length === 0 && allInput) {
        allInput.checked = true;
      }
    }

    applyDropdownFilters() {
      const selectedTags = this.getSelectedDropdownTags();
      const didChange = !this.haveSameTags(selectedTags, this.activeTags);

      this.activeTags = selectedTags;
      this.updateActiveFilter();

      if (this.dropdownDetails) this.dropdownDetails.open = false;

      if (didChange || this.blogsWrapper.children.length === 0) {
        this.loadArticles({ reset: true });
      }
    }

    clearDropdownFilters() {
      const didChange = this.activeTags.length > 0;

      this.activeTags = [];
      this.updateActiveFilter();

      if (this.dropdownDetails) this.dropdownDetails.open = false;

      if (didChange || this.blogsWrapper.children.length === 0) {
        this.loadArticles({ reset: true });
      }
    }

    setLoadingState(isLoading) {
      this.blogsWrapper.toggleAttribute('aria-busy', isLoading);
      this.loadMoreButton.disabled = isLoading;
      this.loadMoreButton.textContent = isLoading
        ? 'Loading…'
        : this.loadMoreText;
    }

    async loadArticles({ reset = false } = {}) {
      if (!reset && (this.isLoading || !this.hasNextPage)) return;

      if (reset) {
        this.activeController?.abort();
        this.endCursor = null;
        this.hasNextPage = true;
        this.renderedArticleIds.clear();
        this.blogsWrapper.replaceChildren();
      }

      const controller = new AbortController();

      this.activeController = controller;
      this.isLoading = true;
      this.statusElement.textContent = 'Loading…';
      this.setLoadingState(true);

      try {
        const articles = await this.fetchArticles({
          tags: this.activeTags,
          after: this.endCursor,
          first: this.pageSize,
          signal: controller.signal,
        });

        if (controller !== this.activeController) return;

        this.renderArticles(articles.nodes || []);

        this.endCursor = articles.pageInfo?.endCursor || null;
        this.hasNextPage = Boolean(
          articles.pageInfo?.hasNextPage && this.endCursor
        );

        if (this.blogsWrapper.children.length === 0) {
          this.statusElement.textContent = 'No articles found.';
        } else if (!this.hasNextPage) {
          this.statusElement.textContent = 'All articles loaded.';
        } else {
          this.statusElement.textContent = '';
        }
      } catch (error) {
        if (error.name === 'AbortError') return;

        console.error(error);
        this.statusElement.textContent = 'Unable to load blog articles.';
      } finally {
        if (controller !== this.activeController) return;

        this.activeController = null;
        this.isLoading = false;
        this.setLoadingState(false);
        this.loadMoreButton.hidden = !this.hasNextPage;
      }
    }
  }

  if (!customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, AocBlogArchive);
  }
})();
